/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useState } from 'react';
import cx from 'classnames';
import { Actionable, Icon, Text, View } from '@az/starc-ui';
import { SddIcon } from './Icons/SddIcon';
import { BopusIcon } from './Icons/BopusIcon';
import { StdDeliveryIcon } from './Icons/StdDeliveryIcon';
import { useFeatureFlag } from '@/features/globalConfig';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { capitalize } from '@/utils/capitalize';
import { FULFILLMENT_OPTION_TO_RENDER } from '@/constants/fulfillmentConstants';
import { useLabels } from '@/hooks/useLabels';
import NddIcon from '@/public/images/next-day-delivery-shelf.svg';
import {
  checkIfDateIsTomorrow,
  generateDeliveryTimePhrase,
} from '../../utils/checkIfDateIsTomorrow';
import { createFreeGroundDateRange } from '../../utils/createFreeGroundDateRange';
import { shouldShowExpedited } from '../../utils/shouldShowExpedited';
import styles from './styles.module.scss';

const iconSelector = (option: string) => {
  switch (option) {
    case FULFILLMENT_OPTION_TO_RENDER.BOPUS_UNAVAILABLE:
    case FULFILLMENT_OPTION_TO_RENDER.BOPUS_AVAILABLE_IN_STORE:
    case FULFILLMENT_OPTION_TO_RENDER.BOPUS_UNAVAILABLE_IN_STORE:
    case FULFILLMENT_OPTION_TO_RENDER.NO_STORE_SELECTED:
      return (
        <Icon
          svg={BopusIcon}
          size={5}
          color="600"
          attributes={{
            'data-testid': 'bopus-icon',
            style: { marginLeft: '8px' },
          }}
        />
      );

    case FULFILLMENT_OPTION_TO_RENDER.NDD_AVAILABLE:
      return (
        <Icon
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          svg={NddIcon}
          size={8}
          color="600"
          attributes={{
            'data-testid': 'ndd-icon',
            style: { margin: '-6px auto' },
          }} // this icon should be the same size as a size={5} <Icon /> hence style attribute
        />
      );
    case FULFILLMENT_OPTION_TO_RENDER.VDP_DS_AVAILABLE_EXPRESS_A:
    case FULFILLMENT_OPTION_TO_RENDER.VDP_DS_AVAILABLE_EXPRESS_B:
    case FULFILLMENT_OPTION_TO_RENDER.VDP_DS_AVAILABLE_GROUND:
    case FULFILLMENT_OPTION_TO_RENDER.STH_UNAVAILABLE:
    case FULFILLMENT_OPTION_TO_RENDER.STD_DELIVERY:
      return (
        <Icon
          svg={StdDeliveryIcon}
          size={5}
          color="600"
          attributes={{
            'data-testid': 'std-delivery-icon',
            style: { marginLeft: '8px' },
          }}
        />
      );

    default:
      return <Icon svg={SddIcon} size={5} color="600" attributes={{ 'data-testid': 'sdd-icon' }} />;
  }
};

type FulfillmentStatesProps = {
  fulfillmentOptionToRender: string;
  fulfillmentTypeLabel?: string;
  address?: string;
  estDeliveryDate?: string;
  openSisterStore?: () => void;
  toggleStoreModal?: () => void;
  fullfillmentCutOffDate?: string;
};

const labelMap = {
  lblTomorrow: 'label_fulfilment_options_tomorrow',
  lblGetIt: 'label_fulfilment_options_get_it',
  lblGetItBy: 'label_fulfilment_options_get_it_by',
  labelHomeDeliveryUnavailable: 'label_new_fulfillment_home_delivery_unavailable',
  labelFree: 'label_fulfilment_options_free',
  label35Plus: 'label_new_fulfillment_35_plus',
  labelUnavailableWithin: 'label_new_fulfillment_pickup_unavailable_within',
  labelMiles: 'label_MyAccountMyProfile_vehicle_Miles',
  labelNotAvailable: 'label_fulfilment_options_not_available',
  labelPickUp: 'label_cart_lineItem_PickUp',
  labelSelectStore: 'label_shelf_select_store',
  labelForPickupAvailability: 'label_new_fulfillment_for_pickup_availability',
  labelExpedited: 'label_expedited_delivery',
  lblCheckNearbyStores: 'label_check_nearby_sister_store',
  lblStorePickup: 'label_store_pickup',
};

export const FulfillmentStates = ({
  fulfillmentOptionToRender,
  fulfillmentTypeLabel,
  address,
  openSisterStore,
  toggleStoreModal,
  estDeliveryDate,
  fullfillmentCutOffDate,
}: FulfillmentStatesProps) => {
  const sisterStoreSearchRadius = useFeatureFlag('SISTER_STORE_SEARCH_RADIUS') ?? '150';
  const testTimeOverride = useMonetateDecisionFlag('testTimeOverride');
  const labels = useLabels(labelMap);

  const [isTomorrow, setIsTomorrow] = useState(false);

  useEffect(() => {
    if (
      fulfillmentOptionToRender === FULFILLMENT_OPTION_TO_RENDER.STD_DELIVERY ||
      fulfillmentOptionToRender === FULFILLMENT_OPTION_TO_RENDER.SDD_DELIVERY
    ) {
      setIsTomorrow(checkIfDateIsTomorrow(estDeliveryDate ?? ''));
    }
  }, [estDeliveryDate, fulfillmentOptionToRender]);

  const messageSelector = (option: string) => {
    switch (option) {
      case FULFILLMENT_OPTION_TO_RENDER.VDP_DS_AVAILABLE_EXPRESS_A: {
        const [expressLabel, deliveryLabel] = fulfillmentTypeLabel?.split(' ') || [];
        const { prepositional, formattedDate } = generateDeliveryTimePhrase({
          estimatedDeliveryDate: estDeliveryDate ?? '',
          labels: {
            lblTomorrow: labels.lblTomorrow,
          },
        });
        return (
          <Text
            size={'087'}
            color="600"
            className={cx(styles.textLineHeight)}
            attributes={{ 'data-testid': 'ndd-available-msg' }}
          >
            <span className={cx([styles.coloredText, styles.greenText])}>{expressLabel}</span>
            <span> {deliveryLabel}</span>
            <span>{` ${prepositional} ${formattedDate}`}</span>
          </Text>
        );
      }

      case FULFILLMENT_OPTION_TO_RENDER.VDP_DS_AVAILABLE_EXPRESS_B: {
        const [expressLabel, deliveryLabel] = fulfillmentTypeLabel?.split(' ') || [];
        const { prepositional, formattedDate } = generateDeliveryTimePhrase({
          estimatedDeliveryDate: estDeliveryDate ?? '',
          labels: {
            lblTomorrow: labels.lblTomorrow,
          },
        });
        return (
          <Text
            size={'087'}
            color="600"
            className={cx(styles.textLineHeight)}
            attributes={{ 'data-testid': 'ndd-available-msg' }}
          >
            <span className={cx([styles.coloredText, styles.greenText])}>{expressLabel}</span>
            <span> {deliveryLabel}</span>
            <span>{` ${prepositional} ${formattedDate}`}</span>
            <span>{` for $4.99`} </span>
          </Text>
        );
      }

      case FULFILLMENT_OPTION_TO_RENDER.VDP_DS_AVAILABLE_GROUND: {
        return (
          <Text
            size={'087'}
            color="600"
            className={cx(styles.textLineHeight)}
            attributes={{ 'data-testid': 'ndd-available-msg' }}
          >
            <span
              className={cx([styles.coloredText, styles.greenText, styles.capitalized])}
            >{`${fulfillmentTypeLabel?.toLocaleLowerCase()} `}</span>
            <span>{`by ${createFreeGroundDateRange(estDeliveryDate)} `}</span>
          </Text>
        );
      }

      case FULFILLMENT_OPTION_TO_RENDER.STH_UNAVAILABLE:
        return (
          <Text
            size={'087'}
            className={cx(styles.textLineHeight, {
              [styles.redText]: option === FULFILLMENT_OPTION_TO_RENDER.STH_UNAVAILABLE,
              [styles.grayText]: option !== FULFILLMENT_OPTION_TO_RENDER.STH_UNAVAILABLE,
            })}
          >
            {option === FULFILLMENT_OPTION_TO_RENDER.STH_UNAVAILABLE
              ? labels.labelHomeDeliveryUnavailable
              : fulfillmentTypeLabel}
          </Text>
        );
      case FULFILLMENT_OPTION_TO_RENDER.STD_DELIVERY:
      case FULFILLMENT_OPTION_TO_RENDER.SDD_DELIVERY: {
        const sddArr = fulfillmentTypeLabel?.split(' ');

        return (
          <Text size={'087'} className={cx(styles.textLineHeight, styles.grayText)}>
            <span className={cx(styles.greenText, styles.coloredText)}>
              {sddArr?.slice(0, -1).join(' ')}
            </span>
            <span> {sddArr?.slice(-1)}</span>
            {option === FULFILLMENT_OPTION_TO_RENDER.STD_DELIVERY && (
              <span className={styles.nonBreakingText}>
                {isTomorrow ? ` by ${labels.lblTomorrow}` : ` by ${estDeliveryDate}`}
              </span>
            )}
          </Text>
        );
      }
      case FULFILLMENT_OPTION_TO_RENDER.NDD_AVAILABLE: {
        const textArr = fulfillmentTypeLabel?.split(' ');
        const showExpedited = shouldShowExpedited(fullfillmentCutOffDate, testTimeOverride);
        const deliveryText = showExpedited
          ? labels.labelExpedited
          : textArr?.slice(0, -1).join(' ');

        return (
          <Text
            size={'087'}
            color="600"
            className={cx(styles.textLineHeight, styles.nonBreakingText)}
            attributes={{ 'data-testid': 'ndd-available-msg' }}
          >
            <span className={cx([styles.coloredText, styles.greenText])}>
              {labels.labelFree} {deliveryText}
            </span>
            <span> {textArr?.slice(-1)}</span>
            <span> {labels.label35Plus}</span>
          </Text>
        );
      }

      case FULFILLMENT_OPTION_TO_RENDER.BOPUS_UNAVAILABLE:
        return (
          <Text
            size={'087'}
            className={cx(styles.redText, styles.textLineHeight)}
            attributes={{ 'data-testid': 'bopus-unavailable-msg' }}
          >
            {`${labels.labelUnavailableWithin} `}
            <span>{sisterStoreSearchRadius}</span>
            <span>{` ${labels.labelMiles.toLowerCase()}`}</span>
          </Text>
        );

      case FULFILLMENT_OPTION_TO_RENDER.BOPUS_UNAVAILABLE_IN_STORE: {
        return (
          <Text
            size={'087'}
            color="600"
            className={styles.textLineHeight}
            attributes={{ 'data-testid': 'bopus-unavailable-in-store-selected-msg' }}
          >
            <div key="not-available">
              <span className={cx([styles.redText])}>{labels.labelNotAvailable}</span>
              <span> at </span>
              <span className={styles.address}>{address}</span>
            </div>
            <div key="pickup-available">
              {
                <Actionable onClick={openSisterStore}>
                  <span>
                    <span className={cx([styles.coloredText, styles.greenText])}>
                      {labels.lblStorePickup}
                    </span>
                    &nbsp;
                    <span className={styles.linkAddress}>
                      <span>{labels.lblCheckNearbyStores}</span>
                    </span>
                  </span>
                </Actionable>
              }
            </div>
          </Text>
        );
      }

      case FULFILLMENT_OPTION_TO_RENDER.NO_STORE_SELECTED:
        return (
          <Actionable onClick={toggleStoreModal}>
            <Text
              size={'087'}
              color="600"
              className={styles.textLineHeight}
              attributes={{ 'data-testid': 'bopus-no-store-selected-msg' }}
            >
              <span className={styles.linkAddress}>{labels.labelSelectStore}</span>
              <span>{` ${labels.labelForPickupAvailability} `}</span>
            </Text>
          </Actionable>
        );

      default:
        return (
          <Actionable onClick={toggleStoreModal}>
            <Text
              size={'087'}
              color="600"
              className={styles.textLineHeight}
              attributes={{ 'data-testid': 'bopus-available-in-store-selected-msg' }}
            >
              <span className={cx([styles.coloredText, styles.greenText])}>
                {capitalize(labels.labelPickUp)}
              </span>
              <span> at </span>
              <span className={cx(styles.linkAddress, styles.address)}>{address}</span>
            </Text>
          </Actionable>
        );
    }
  };

  return (
    <View direction="row" className={styles.stateContainer}>
      <View.Item className={styles.iconSpace}>{iconSelector(fulfillmentOptionToRender)}</View.Item>
      <View.Item className={styles.textContainer}>
        {messageSelector(fulfillmentOptionToRender)}
      </View.Item>
    </View>
  );
};
